import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NetSuiteBillPayload, OcrResponse, OcrResultItem } from '../types';
import StructuredView from './StructuredView';
import NetSuiteBillDialog, { BillSubmission } from './NetSuiteBillDialog';
import { downloadCsv, downloadJson, printElement } from '../utils/export';
import { buildDisplayModel } from '../utils/display';
import { canCreateBill, extractTotals, mapOcrToBill } from '../utils/netsuite';

interface ResultViewerProps {
  response: OcrResponse;
}

type ViewMode = 'table' | 'json';

/** A small "Export ▾" dropdown that closes on outside-click or Escape. */
const ExportMenu: React.FC<{
  onPrint: () => void;
  onCsv: () => void;
  onJson: () => void;
}> = ({ onPrint, onCsv, onJson }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <div className="exportmenu" ref={ref}>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Export ▾
      </button>
      {open && (
        <div className="menu" role="menu">
          <button type="button" className="menu__item" role="menuitem" onClick={run(onPrint)}>
            <span className="menu__ico">🖨️</span> Print / Save as PDF
          </button>
          <button type="button" className="menu__item" role="menuitem" onClick={run(onCsv)}>
            <span className="menu__ico">📊</span> Excel (CSV)
          </button>
          <button type="button" className="menu__item" role="menuitem" onClick={run(onJson)}>
            <span className="menu__ico">{'{ }'}</span> JSON file
          </button>
        </div>
      )}
    </div>
  );
};

const ResultCard: React.FC<{
  item: OcrResultItem;
  index: number;
  total: number;
  docTypeLabel: string;
  open: boolean;
  onToggle: (index: number) => void;
}> = ({ item, index, total, docTypeLabel, open, onToggle }) => {
  const [view, setView] = useState<ViewMode>('table');
  const [showEmpty, setShowEmpty] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // NetSuite bill. The draft lives here rather than in the dialog so closing
  // the dialog never throws away edits.
  const [billOpen, setBillOpen] = useState(false);
  const [billDraft, setBillDraft] = useState<NetSuiteBillPayload | null>(null);
  const [billSubmission, setBillSubmission] = useState<BillSubmission | null>(null);
  const billable = item.success && canCreateBill(docTypeLabel);
  const extractedTotals = useMemo(() => extractTotals(item.data), [item.data]);

  // Re-extracting the same file reuses this card (same key): start over.
  useEffect(() => {
    setBillOpen(false);
    setBillDraft(null);
    setBillSubmission(null);
  }, [item]);

  const openBill = () => {
    if (!billDraft) setBillDraft(mapOcrToBill(item.data, item.filename));
    setBillOpen(true);
  };

  const json = item.success ? JSON.stringify(item.data, null, 2) : null;

  // A one-line summary shown when the card is collapsed, so a document can be
  // recognised without expanding it.
  const summary = useMemo(() => {
    if (!item.success) return null;
    const m = buildDisplayModel(item.data, docTypeLabel, {});
    const primary = m.totals.find((t) => t.primary) || m.totals[m.totals.length - 1];
    const amount = primary ? `${m.currency ? `${m.currency} ` : ''}${primary.value}` : '';
    return { title: m.title, amount };
  }, [item, docTypeLabel]);

  // Only offer collapsing when there is more than one document AND it succeeded
  // (errors always stay visible).
  const collapsible = total > 1 && item.success;
  const isOpen = collapsible ? open : true;

  const copyJson = async () => {
    if (json == null) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (non-secure context) */
    }
  };

  const doPrint = () => {
    if (collapsible && !open) onToggle(index); // must be expanded to print
    setView('table'); // print always shows the structured view
    window.requestAnimationFrame(() => printElement(cardRef.current));
  };

  const headerClick = () => {
    if (collapsible) onToggle(index);
  };
  const headerKey = (e: React.KeyboardEvent) => {
    if (collapsible && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle(index);
    }
  };

  return (
    <div className={`result__card${isOpen ? '' : ' result__card--collapsed'}`} ref={cardRef}>
      <div
        className={`result__card-header${collapsible ? ' result__card-header--clickable' : ''}`}
        onClick={headerClick}
        onKeyDown={headerKey}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
      >
        {collapsible && (
          <span className={`result__chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true">
            ▶
          </span>
        )}
        <div className="result__cardtitle">
          <span className="result__filename" title={item.filename}>
            {total > 1 ? `${index + 1}. ` : ''}
            {item.filename}
            {item.meta ? <span className="muted"> · {item.meta.processingMs} ms</span> : null}
          </span>
          {collapsible && !isOpen && summary && (
            <span className="result__summary">
              <strong>{summary.title}</strong>
              {summary.amount ? ` · ${summary.amount}` : ''}
              {billSubmission && <span className="result__bill-created"> · ✓ Bill in NetSuite</span>}
            </span>
          )}
        </div>

        {isOpen && item.success && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div className="result__toolbar" onClick={(e) => e.stopPropagation()}>
            <div className="viewtoggle" role="tablist" aria-label="View mode">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'table'}
                className={`viewtoggle__btn${view === 'table' ? ' is-active' : ''}`}
                onClick={() => setView('table')}
              >
                Table
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'json'}
                className={`viewtoggle__btn${view === 'json' ? ' is-active' : ''}`}
                onClick={() => setView('json')}
              >
                JSON
              </button>
            </div>
            <ExportMenu
              onPrint={doPrint}
              onCsv={() => downloadCsv(item.data, docTypeLabel, item.filename)}
              onJson={() => downloadJson(item.data, item.filename)}
            />
            {billable && (
              <button
                type="button"
                className={`btn btn--sm btn--netsuite${billSubmission ? ' is-done' : ''}`}
                onClick={openBill}
                aria-haspopup="dialog"
              >
                {billSubmission ? '✓ Bill created' : 'Create bill in NetSuite'}
              </button>
            )}
          </div>
        )}
      </div>

      {isOpen &&
        (!item.success ? (
          <p className="banner banner--error">
            {item.error?.message || 'Failed to process this file.'}
            {item.error?.code ? ` (${item.error.code})` : ''}
          </p>
        ) : view === 'table' ? (
          <div className="result__structured">
            <StructuredView data={item.data} docTypeLabel={docTypeLabel} includeEmpty={showEmpty} />
            <div className="result__cardfoot">
              <label className="result__toggle-empty">
                <input
                  type="checkbox"
                  checked={showEmpty}
                  onChange={(e) => setShowEmpty(e.target.checked)}
                />
                Show empty fields
              </label>
            </div>
          </div>
        ) : (
          <div className="result__jsonwrap">
            <div className="result__jsonbar">
              <button type="button" className="btn btn--ghost btn--sm" onClick={copyJson}>
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
            </div>
            <pre className="result__json">{json}</pre>
          </div>
        ))}

      {billOpen && billDraft && (
        <NetSuiteBillDialog
          filename={item.filename}
          draft={billDraft}
          onDraftChange={setBillDraft}
          onReset={() => setBillDraft(mapOcrToBill(item.data, item.filename))}
          extracted={extractedTotals}
          submission={billSubmission}
          onSubmitted={setBillSubmission}
          onClose={() => setBillOpen(false)}
        />
      )}
    </div>
  );
};

const ResultViewer: React.FC<ResultViewerProps> = ({ response }) => {
  const total = response.results.length;
  const docTypeLabel = response.docType || 'Result';

  // With several documents, start with only the first expanded so the user is
  // not forced to scroll past every result. A single document is always open.
  const initial = (r: OcrResponse) => r.results.map((_, i) => r.results.length === 1 || i === 0);
  const [open, setOpen] = useState<boolean[]>(() => initial(response));
  useEffect(() => {
    setOpen(initial(response));
  }, [response]);

  const toggle = (i: number) => setOpen((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const expandAll = () => setOpen(response.results.map(() => true));
  const collapseAll = () => setOpen(response.results.map(() => false));
  const openCount = open.filter(Boolean).length;

  return (
    <section className="result">
      <div className="result__header">
        <div>
          <h2>Extracted data</h2>
          <p className="muted">
            {response.docType} · {response.model}
            {total > 1 ? ` · ${response.succeeded ?? total}/${total} succeeded` : ''}
          </p>
        </div>
        {total > 1 && (
          <div className="result__bulk">
            <button
              type="button"
              className="link-btn"
              onClick={expandAll}
              disabled={openCount === total}
            >
              Expand all
            </button>
            <span className="app__footer-sep">·</span>
            <button
              type="button"
              className="link-btn"
              onClick={collapseAll}
              disabled={openCount === 0}
            >
              Collapse all
            </button>
          </div>
        )}
      </div>
      <div className="result__cards">
        {response.results.map((item, i) => (
          <ResultCard
            key={`${item.filename}:${i}`}
            item={item}
            index={i}
            total={total}
            docTypeLabel={docTypeLabel}
            open={open[i] ?? true}
            onToggle={toggle}
          />
        ))}
      </div>
    </section>
  );
};

export default ResultViewer;
